// THE money formatter. Every amount on screen goes through here.
// NOTE: the DB column is named `value_inr` for historical reasons, but it stores
// the amount in the ORDER's own currency (EUR, USD, ...), NOT rupees. Pass the
// row's currency when it has one; without it the WORKSPACE's currency is used
// (lib/workspace-display), never a blanket rupee.
import { workspaceCurrency } from './workspace-display';

// Indian digit grouping (1,00,000) belongs to rupees; every other currency
// groups in thousands.
const localeFor = (cur) => (cur === 'INR' ? 'en-IN' : 'en-US');
const curOf = (currency) => String(currency || workspaceCurrency() || 'INR').toUpperCase();

export function money(value, currency, { decimals = 2 } = {}) {
  const cur = curOf(currency);
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(localeFor(cur), {
      style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: decimals,
    }).format(n);
  } catch {
    return `${cur} ${n.toLocaleString(localeFor(cur))}`;
  }
}

/**
 * A money cell: '-' for no value, whole units by default. Replaces formatINR,
 * which printed every amount as rupees whatever the workspace billed in.
 */
export function formatMoney(value, { decimals = 0, currency } = {}) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '-';
  return money(Number(value), currency, { decimals });
}

/** The three-letter code amounts are in: the row's own, else the workspace's. */
export function currencyCode(currency) { return curOf(currency); }

/**
 * An input's label with its currency: "Deal value (AED)". The code, never a
 * symbol, because "$" does not say which dollar and a label is read once.
 */
export function withCurrency(label, currency) { return `${label} (${curOf(currency)})`; }

const SYMBOLS = { INR: '₹', EUR: '€', USD: '$', GBP: '£', AED: 'AED ', SAR: 'SAR ', AUD: 'A$', CAD: 'C$', SGD: 'S$' };
export function currencySymbol(currency) {
  const cur = curOf(currency);
  if (SYMBOLS[cur]) return SYMBOLS[cur];
  try { return (0).toLocaleString('en', { style: 'currency', currency: cur }).replace(/[\d.,\s]/g, '') || `${cur} `; }
  catch { return `${cur} `; }
}

// Abbreviated money for headline figures. INR uses Cr/L/k; other currencies use
// B/M/k. Symbol follows the currency.
export function moneyShort(value, currency) {
  const cur = curOf(currency);
  const s = currencySymbol(cur);
  const v = Number(value) || 0;
  const a = Math.abs(v);
  if (cur === 'INR') {
    if (a >= 1e7) return `${s}${(v / 1e7).toFixed(2)}Cr`;
    if (a >= 1e5) return `${s}${(v / 1e5).toFixed(2)}L`;
    if (a >= 1e3) return `${s}${(v / 1e3).toFixed(1)}k`;
    return `${s}${small(v)}`;
  }
  if (a >= 1e9) return `${s}${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}${(v / 1e3).toFixed(1)}k`;
  return `${s}${small(v)}`;
}

// Under a thousand there is nothing to abbreviate, so rounding only loses money:
// a 32.38 order was rendering as 32. Keep the cents when there are any, and drop
// them when there are not, so a clean 40 does not become "40.00".
function small(v) {
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2)));
}

// The dominant currency across a set of order-like rows ({ currency }). Used for
// aggregate figures (a workspace is single-currency in practice).
export function dominantCurrency(rows, fallback = workspaceCurrency()) {
  const counts = {};
  for (const r of rows || []) {
    const c = (r?.currency || '').toUpperCase();
    if (c) counts[c] = (counts[c] || 0) + 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : fallback;
}
